/**
 * Utility functions for sending messages to the companion app via native bridge for Station
 */

/**
 * Sends a message to the companion app via the native bridge (iOS/macOS)
 * @param message - The napster-companion:// protocol message to send
 * @returns true if message was sent successfully, false otherwise
 */
export const sendCompanionMessage = (message: string): boolean => {
  if (
    typeof window === 'undefined' ||
    !window.webkit?.messageHandlers?.appBridge
  ) {
    return false;
  }

  try {
    console.log('sending bridge message', message);
    window.webkit.messageHandlers.appBridge.postMessage(message);
    return true;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Failed to send message to companion app bridge:', error);
    }
    return false;
  }
};

/**
 * Sends a start-over message to the companion app
 */
export const sendStartOverMessage = (): boolean => {
  console.log('sendStartOverMessage');
  return sendCompanionMessage('napster-companion://companion/start-over');
};

/**
 * Sends a select companion message to the companion app
 * @param companionId - The companion ID to select
 */
export const sendSelectCompanionMessage = (companionId: string): boolean => {
  console.log('sendSelectCompanionMessage', companionId);
  return sendCompanionMessage(
    `napster-companion://companion/select/${companionId}`,
  );
};

/**
 * Sends a speak message to the companion app with data
 * @param data - The data object to send
 */
export const sendSpeakMessage = (data: unknown): boolean => {
  const urlSchema = `napster-companion://companion/speak?data=${encodeURIComponent(
    JSON.stringify(data),
  )}`;
  console.log('sendSpeakMessage', urlSchema);
  return sendCompanionMessage(urlSchema);
};

/**
 * Sends a stop-talking message to the companion app
 */
export const sendStopTalkingMessage = (): boolean => {
  console.log('sendStopTalkingMessage');
  return sendCompanionMessage('napster-companion://companion/stop-talking');
};

/**
 * Sends a search results message to the companion app with data
 * @param data - The data object to send
 */
export const sendSearchResultsMessage = (data: unknown): boolean => {
  const urlSchema = `napster-companion://companion/search-results?data=${encodeURIComponent(
    JSON.stringify(data),
  )}`;
  console.log('sendSearchResultsMessage', urlSchema);
  return sendCompanionMessage(urlSchema);
};

/**
 * Sends a print message to the companion app with a base64 image data URL
 * @param base64DataUrl - The image as a base64 data URL (e.g., "data:image/png;base64,...")
 */
export const sendPrintMessage = (base64DataUrl: string): boolean => {
  const encodedData = encodeURIComponent(base64DataUrl);
  const urlSchema = `napster-companion://companion/print/${encodedData}`;
  console.log('sendPrintMessage', urlSchema);
  return sendCompanionMessage(urlSchema);
};

export const updateNativeCompanionVolume = (volume: number): boolean => {
  const urlSchema = `napster-companion://companion/volume/${volume}`;
  return sendCompanionMessage(urlSchema);
};

export const muteNativeCompanion = (muted: boolean): boolean => {
  const urlSchema = `napster-companion://companion/mute/${
    muted ? 'true' : 'false'
  }`;
  return sendCompanionMessage(urlSchema);
};

export const sendStartHostMessage = (): boolean => {
  console.log('sendStartHostMessage');
  return sendCompanionMessage('napster-companion://companion/start-host');
};
